import React, { useContext, useMemo } from 'react';

import { Badge, Col, Typography } from 'antd';

import StatCard from '@components/StatCard';

import {
  CalendarOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  PieChartOutlined,
  DashboardOutlined,
} from '@ant-design/icons';

import { IDevice } from '@pages/Assets/Inventory/types';

import { FaNetworkWired } from 'react-icons/fa';
import { BiBlock } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import { getLocaleString } from '@utils/locales';
import { bytesFormat, extractDateInISOFormat } from '@utils/formatters';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import SessionContext from '@providers/Session/SessionContext';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import CustomCard from '@components/CustomCard';
import useSwr from '@hooks/useSwr';
import CustomTag from '@components/CustomTag';
import { ContainerRow } from './styles';

const { Paragraph } = Typography;

interface ITBDetails {
  device: IDevice;
  handleUpdateNote: (value: string) => void;
  observation?: string;
  isConnectOne: boolean;
  showContracted?: string;
}

const TabPanesDetails: React.FC<ITBDetails> = ({
  device: deviceProps,
  handleUpdateNote,
  observation,
  isConnectOne,
  showContracted,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['inventory-detailed', 'inventory', 'customer-contracts']);
  const { t: tStatus } = useTranslation('status');
  const { isMot, isMotPay, isRoot } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const customerType = keycloak.idTokenParsed?.customerType;
  const isCustomer = customerType === 'CUSTOMER';
  const isRealAccount =
    (isMot && showContracted === 'true' && !isCustomer) || isRoot;

  const { data: contractData } = useSwr('/service-proxy/contracts/customer', {
    params: {
      assetNumber: deviceProps?.contractAssetNumber,
      page: 0,
      linesPerPage: 1,
    },
  });

  const contractInfo = useMemo(
    () => contractData?.content?.[0],
    [contractData?.content],
  );

  return (
    <ContainerRow gutter={[12, 12]}>
      <RoleChecker client="inventory" role={rolesConstants.INVENT_STATUS}>
        <Col lg={12} xs={24}>
          <StatCard
            title={t('status')}
            value={`${tStatus(deviceProps?.status || '')}` || '-'}
            icon={
              <Badge
                status={
                  deviceProps?.status
                    ? tStatus(`badgeStatus.${deviceProps?.status}`)
                    : 'error'
                }
              />
            }
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>
      <RoleChecker client="inventory" role={rolesConstants.INVENT_OPERATOR}>
        {isMot || isConnectOne ? (
          <>
            <Col lg={12} xs={24}>
              <StatCard
                title={t('operator')}
                icon={<PhoneOutlined />}
                value={deviceProps?.operator || deviceProps?.subOperator || '-'}
                loading={!deviceProps}
              />
            </Col>
            <Col lg={12} xs={24}>
              <StatCard
                title={t('connected-operator')}
                icon={<PhoneOutlined />}
                value={deviceProps?.connectedOperator || '-'}
                loading={!deviceProps}
              />
            </Col>
          </>
        ) : (
          <>
            <Col lg={12} xs={24}>
              <StatCard
                title={
                  deviceProps?.connectedOperator
                    ? t('connected-operator')
                    : t('operator')
                }
                icon={<PhoneOutlined />}
                value={
                  deviceProps?.connectedOperator || deviceProps?.operator || '-'
                }
                loading={!deviceProps}
              />
            </Col>
          </>
        )}
      </RoleChecker>

      {deviceProps.isca && (
        <>
          <RoleChecker client="inventory" role={rolesConstants.INVENT_ISCA}>
            <Col lg={12} xs={24}>
              <StatCard
                title={t('inventory:table.planType')}
                value="Isca"
                icon={<FileTextOutlined />}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_CANCELLATION_DATE}
          >
            <Col lg={12} xs={24}>
              <StatCard
                title={t('inventory:table.cancellationDate')}
                value={
                  deviceProps && deviceProps.cancellationDate
                    ? extractDateInISOFormat(deviceProps.cancellationDate)
                    : '-'
                }
                icon={<CalendarOutlined />}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
        </>
      )}

      <RoleChecker client="inventory" role={rolesConstants.INVENT_ONLINE}>
        <Col lg={12} xs={24}>
          <StatCard
            title={t('connection-status')}
            icon={
              <Badge
                status={tStatus(
                  deviceProps?.online
                    ? 'badgeStatus.ONLINE'
                    : 'badgeStatus.OFFLINE',
                )}
              />
            }
            value={
              deviceProps?.online
                ? t<string>('ONLINE') ?? ''
                : t<string>('OFFLINE')
            }
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>
      <RoleChecker
        client="inventory"
        role={rolesConstants.INVENT_LAST_CONNECTION}
      >
        <Col lg={12} xs={24}>
          <StatCard
            title={t('last-connection')}
            value={
              deviceProps && deviceProps.lastConnection
                ? new Date(deviceProps.lastConnection).toLocaleString(
                    getLocaleString(language),
                  )
                : '-'
            }
            icon={<CalendarOutlined />}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>
      <RoleChecker
        client="inventory"
        role={rolesConstants.INVENT_ACTIVATION_DATE}
      >
        <Col lg={12} xs={24}>
          <StatCard
            title={t('activation-date')}
            value={
              deviceProps && deviceProps.activationDate
                ? extractDateInISOFormat(deviceProps.activationDate)
                : '-'
            }
            icon={<CalendarOutlined />}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>

      <RoleChecker
        client="inventory"
        role={rolesConstants.INVENT_NETWORK_BLOCK_STATUS}
      >
        <Col lg={12} xs={24}>
          <StatCard
            title={t('blocking-status')}
            value={
              `${t(
                `inventory:lock-status.types.${
                  deviceProps?.networkBlockStatus ?? 'NONE'
                }`,
              )}` || '-'
            }
            icon={
              <BiBlock
                style={{
                  fontSize: '1.1rem',
                  color:
                    (deviceProps?.networkBlockStatus ?? 'NONE') === 'NONE'
                      ? 'var(--primary-color)'
                      : 'var(--red-color)',
                }}
              />
            }
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>

      <RoleChecker client="inventory" role={rolesConstants.INVENT_CONSUMPTION}>
        <Col lg={12} xs={24}>
          <StatCard
            title={t('consumption')}
            icon={<PieChartOutlined />}
            value={bytesFormat(deviceProps?.consumptionTotal ?? '0')}
            suffix={
              deviceProps?.franchise
                ? `/ ${bytesFormat(deviceProps?.franchise)}`
                : ''
            }
            loading={!deviceProps}
            style={{ marginBottom: contractInfo?.limiter ? 4 : 0 }}
          />
        </Col>
      </RoleChecker>

      {contractInfo?.limiter && (
        <Col lg={12} xs={24}>
          <StatCard
            title={t('customer-contracts:table.limiter.label')}
            icon={<DashboardOutlined />}
            // @ts-ignore
            value={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CustomTag
                  color={contractInfo?.limiter === 'Ativo' ? 'green' : 'red'}
                >
                  {t(
                    `customer-contracts:table.limiter.${
                      contractInfo?.limiter === 'Ativo' ? 'active' : 'inactive'
                    }`,
                  )}
                </CustomTag>
                {bytesFormat(contractInfo?.limitingValue)}
              </div>
            }
          />
        </Col>
      )}

      {deviceProps?.technology && (
        <Col lg={12} xs={24}>
          <StatCard
            title={t('inventory:table.technology')}
            value={deviceProps?.technology}
            icon={
              <FaNetworkWired
                style={{
                  fontSize: '1.1rem',
                }}
              />
            }
            loading={!deviceProps}
          />
        </Col>
      )}
      {deviceProps?.deviceNumber && isMotPay && (
        <Col lg={12} xs={24}>
          <StatCard
            title={t('inventory:table.deviceNumber')}
            value={deviceProps?.deviceNumber}
            icon={
              <PhoneOutlined
                style={{
                  fontSize: '1.1rem',
                }}
              />
            }
            loading={!deviceProps}
          />
        </Col>
      )}
      {deviceProps?.ggsn && isMotPay && (
        <Col lg={12} xs={24}>
          <StatCard
            title="GGSN"
            value={deviceProps?.ggsn}
            icon={
              <PhoneOutlined
                style={{
                  fontSize: '1.1rem',
                }}
              />
            }
            loading={!deviceProps}
          />
        </Col>
      )}
      <RoleChecker client="inventory" role={rolesConstants.INVENT_CITY}>
        <Col span={24}>
          <StatCard
            title={t('city')}
            value={(() => {
              if (deviceProps) {
                if (deviceProps.city && deviceProps.state) {
                  return `${deviceProps.city}, ${deviceProps.state}`;
                }
                if (deviceProps.city) {
                  return deviceProps.city;
                }
                if (deviceProps.state) {
                  return deviceProps.state;
                }
                return '-';
              }
              return '-';
            })()}
            icon={<EnvironmentOutlined />}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>
      {isMot ? (
        <RoleChecker
          client="inventory"
          role={rolesConstants.INVENT_ACCOUNT_DES}
        >
          <Col span={24}>
            <StatCard
              title={t(isRealAccount ? 'plan' : 'request')}
              icon={<FileTextOutlined />}
              value={
                (isRealAccount
                  ? deviceProps?.plan
                  : deviceProps?.accountDesc) || '-'
              }
              loading={!deviceProps}
            />
          </Col>
        </RoleChecker>
      ) : (
        <RoleChecker client="inventory" role={rolesConstants.INVENT_PLAN}>
          <Col span={24}>
            <StatCard
              title={t('plan')}
              icon={<FileTextOutlined />}
              value={deviceProps?.plan || '-'}
              loading={!deviceProps}
            />
          </Col>
        </RoleChecker>
      )}

      <RoleChecker client="inventory" role={rolesConstants.INVENT_ICCID}>
        <Col lg={12} xs={24}>
          <StatCard
            title="ICCID"
            value={deviceProps?.iccid || '-'}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>

      <RoleChecker client="inventory" role={rolesConstants.INVENT_IMEI}>
        <Col lg={12} xs={24}>
          <StatCard
            title="IMEI"
            value={deviceProps?.imei || '-'}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>
      <RoleChecker client="inventory" role={rolesConstants.INVENT_IMSI}>
        <Col lg={12} xs={24}>
          <StatCard
            title="IMSI"
            value={deviceProps?.imsi || '-'}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>

      <RoleChecker client="inventory" role={rolesConstants.INVENT_MSISDN}>
        <Col lg={12} xs={24}>
          <StatCard
            title="MSISDN"
            value={deviceProps?.msisdn || '-'}
            loading={!deviceProps}
          />
        </Col>
      </RoleChecker>
      <RoleChecker client="inventory" role={rolesConstants.INVENT_OBSERVATION}>
        <Col span={24}>
          <CustomCard loading={!deviceProps} title={t('note')}>
            <Paragraph editable={{ onChange: handleUpdateNote }}>
              {observation ?? deviceProps?.observation}
            </Paragraph>
          </CustomCard>
        </Col>
      </RoleChecker>
    </ContainerRow>
  );
};

export default TabPanesDetails;
